import { GlobalInsightsResponse, InsightsDateRange } from '@/components/globalPrompts/insights/insightsTypes';
import { API_ENDPOINTS, buildApiUrl, QUERY_CONFIG } from '@/constants';
import { api, getErrorMessage } from '@/utils';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const GLOBAL_PROMPT_INSIGHTS_QUERY_KEY = ['api', 'global-prompts', 'insights'] as const;

/**
 * @param enabled  Set to false to skip the request when there are no global prompt questions.
 */
const useFetchGlobalPromptInsights = (dateRange: InsightsDateRange, projectIds: number[] = [], enabled = true) => {
    const { data, isLoading, isFetching, isError, error, refetch } = useQuery<GlobalInsightsResponse, AxiosError>({
        queryKey: [...GLOBAL_PROMPT_INSIGHTS_QUERY_KEY, dateRange, projectIds.join(',') || 'all'],
        queryFn: async () => {
            const params: Record<string, string | number> = { date_range: dateRange };

            if (projectIds.length > 0) {
                params.project_id = projectIds.join(',');
            }

            const url = buildApiUrl(API_ENDPOINTS.GLOBAL_PROMPT_INSIGHTS, params);
            const response = await api.get(url);

            return response.data as GlobalInsightsResponse;
        },
        enabled,
        staleTime: 0,
        // Keep the previous results on screen while the new selection loads.
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        retry: 2,
    });

    return {
        insights: data ?? null,
        isError,
        isLoading,
        isFetching,
        errorMessage: getErrorMessage(error),
        refetch,
    };
};

export default useFetchGlobalPromptInsights;
